import { useAuthorizationServiceV1 } from "@rw1/api-services";
import { Authorization_V1 } from "@rw1/auto-generated-clients";
import { useMemo } from "react";
import { STALE_TIME_MS } from "../../../hooks/constants";
import { QueryKeys } from "../../../hooks/QueryKeys";
import { useFetchAll } from "../../../hooks/useFetchAll";

export interface UseAllUserRolesForOrganizationOptions {
    organizationId?: string,
    enabled?: boolean;
}

interface UseAllUserRolesForOrganizationResult {
    userRoles: Authorization_V1.UserRole[];
    isLoading: boolean;
    error: Error | null;
}

export const useAllUserRolesForOrganization = (
    options: UseAllUserRolesForOrganizationOptions = {},
): UseAllUserRolesForOrganizationResult => {
    const { organizationId, enabled } = options;
    const authorizationClientV1 = useAuthorizationServiceV1();
    if (!organizationId) {
        throw new Error("Organization ID is required");
    }

    const {
        data: userRoles,
        isLoading,
        error,
    } = useFetchAll<Authorization_V1.UserRole>(
        [QueryKeys.OrganizationUserRoles, organizationId],
        (page, size) =>
            authorizationClientV1.getAllOrganizationUserRoles(
                organizationId,
                page,
                size
            ),
        {
            enabled: !!enabled && !!organizationId,
            staleTime: STALE_TIME_MS,
        },
    );

    return useMemo(
        () => ({
            userRoles: userRoles ?? [],
            isLoading: isLoading || (!!organizationId && userRoles === undefined && !error),
            error,
        }),
        [userRoles, isLoading, error, organizationId],
    );
};
